import React, { useEffect } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl-next';

import Button from '@atlaskit/button';
import MoreIcon from '@atlaskit/icon/glyph/more';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import {
	OnboardingWarningBanner,
	BannerLink,
} from '@confluence/banners/entry-points/OnboardingWarningBanner';

import { NON_COMPLIANT_BANNER_HEIGHT } from './GracePeriodBanner';

const i18n = defineMessages({
	deactivationWarning: {
		id: 'trial-components.banner.warning.deactivation',
		defaultMessage:
			'Your trial has ended and you‘ve exceeded the user limit. This site will be deactivated on {deactivationDateLong} unless you add billing information to upgrade, or remove {numberOfUsersToDelete, plural, one {# user} other {# users}}.',
		description:
			'inform the user the trial has ended and they exceed the free plan limits. user needs to take action or the site will be deactivated',
	},
	linkToAddBillingV2: {
		id: 'trial-components.banner.add-billing.link.v2',
		defaultMessage: 'Add payment details',
		description: 'CTA link to add payment details',
	},
	linkToLearnMore: {
		id: 'trial-components.banner.learn-more.link',
		defaultMessage: 'Learn more',
		description: 'link to learn more',
	},
});

const LEARN_MORE_LINK =
	'https://support.atlassian.com/confluence-cloud/docs/removing-users-and-downgrading-to-confluence-free';

interface NonCompliantBannerData {
	linkToAddBilling: string;
	deactivationDate: string;
	numberOfUsersOverFreeLimit: number;
	licenseConsumingUserCount: number;
	edition: string;
}

export const NonCompliantBannerMessage = ({
	linkToAddBilling,
	deactivationDate,
	numberOfUsersOverFreeLimit,
	licenseConsumingUserCount,
	edition,
}: NonCompliantBannerData) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		createAnalyticsEvent({
			type: 'sendScreenEvent',
			data: {
				name: 'gracePeriodBanner',
				attributes: {
					deactivationDate,
					licenseConsumingUserCount,
					edition,
				},
			},
		}).fire();
	}, [createAnalyticsEvent, deactivationDate, licenseConsumingUserCount, edition]);

	const onBillingClicked = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'gracePeriodBannerBilling',
				source: 'gracePeriodBanner',
				attributes: {
					deactivationDate,
					licenseConsumingUserCount,
					edition,
				},
			},
		}).fire();
	};

	const onDropdownClicked = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'gracePeriodBannerDropDown',
				source: 'gracePeriodBanner',
			},
		}).fire();
	};

	const onLearnMoreClicked = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'gracePeriodBannerLearnMore',
				source: 'gracePeriodBanner',
			},
		}).fire();
	};

	return (
		<OnboardingWarningBanner
			bannerHeight={NON_COMPLIANT_BANNER_HEIGHT}
			description={
				<FormattedMessage
					{...i18n.deactivationWarning}
					values={{
						deactivationDateLong: deactivationDate,
						numberOfUsersToDelete: numberOfUsersOverFreeLimit,
					}}
				/>
			}
			inlineDescriptionCTA={
				<BannerLink href={linkToAddBilling} target="_blank" onClick={onBillingClicked}>
					<FormattedMessage {...i18n.linkToAddBillingV2} />
				</BannerLink>
			}
			rightSideComponent={
				<DropdownMenu
					trigger={({ triggerRef, ...props }) => (
						<Button
							{...props}
							appearance="subtle"
							iconBefore={<MoreIcon label="more" />}
							ref={triggerRef}
							onClick={(e) => {
								if (props.onClick) {
									props.onClick(e);
								}
								onDropdownClicked();
							}}
						/>
					)}
				>
					<DropdownItemGroup>
						<DropdownItem
							onClick={() => {
								window.open(LEARN_MORE_LINK, '_blank');
								onLearnMoreClicked();
							}}
						>
							<FormattedMessage {...i18n.linkToLearnMore} />
						</DropdownItem>
					</DropdownItemGroup>
				</DropdownMenu>
			}
		/>
	);
};
